import type { ElementRef } from "./properties.ts";
import { ownedSections } from "./sectionSchema.ts";
import type { Point, SwmmLink, SwmmNode, SwmmProject, SwmmSubcatchment } from "./types.ts";
import { polygonCentroid } from "./bounds.ts";
import { nextAvailableName } from "./nextAvailableName.ts";
import { DEFAULT_ELEMENT_DEFAULTS, type ElementDefaults } from "../elementDefaults.ts";

function cloneSection(raw: ReadonlyMap<string, string[][]>, section: string): string[][] {
  return (raw.get(section) ?? []).map((row) => [...row]);
}

/** Upserts `name`'s single point in a [COORDINATES]/[SYMBOLS]-shaped section — used both to move an existing node/raingage and to place a brand new one. */
function setSinglePoint(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  name: string,
  point: Point
): Map<string, string[][]> {
  const rows = cloneSection(raw, section);
  const index = rows.findIndex((row) => row[0] === name);
  const row = [name, String(point.x), String(point.y)];
  if (index === -1) rows.push(row);
  else rows[index] = row;

  const newRaw = new Map(raw);
  newRaw.set(section, rows);
  return newRaw;
}

/** Links follow automatically next render since their path is always read live from the node's own [COORDINATES]. */
export function moveNode(
  raw: ReadonlyMap<string, string[][]>,
  ref: ElementRef,
  point: Point
): Map<string, string[][]> {
  const section = ref.category === "raingage" ? "SYMBOLS" : "COORDINATES";
  return setSinglePoint(raw, section, ref.name, point);
}

function removeRowsByName(rows: string[][], name: string): string[][] {
  return rows.filter((row) => row[0] !== name);
}

/**
 * `[TAGS]` rows are `<ObjectType> <ID> <Tag>` — a genuinely different shape
 * from every other owned section (keyed by row[0]=name), so it can't just
 * join `ownedSections`' own generic row[0]-matching list. Raingages have no
 * real TAGS object type at all (real SWMM only supports Subcatch/Node/Link),
 * hence `| undefined`.
 */
const TAGS_OBJECT_TYPE: Record<"node" | "link" | "subcatchment" | "raingage", string | undefined> =
  {
    node: "Node",
    link: "Link",
    subcatchment: "Subcatch",
    raingage: undefined,
  };

/** Deleting an element used to leave its own `[TAGS]` row behind — invisible until a later element happened to be re-created under the same name and silently inherited the stale tag. */
function removeTagsRow(rows: string[][], objectType: string, name: string): string[][] {
  return rows.filter((row) => !(row[0] === objectType && row[1] === name));
}

/**
 * Deliberately NEVER cascades to other elements on its own — App.tsx's own
 * delete flow decides whether a node CAN be deleted at all (blocked while
 * any link still attaches to it) and whether deleting a link should also
 * take an endpoint node with it. Same "block, don't silently cascade"
 * policy for a subcatchment/raingage still referenced elsewhere — real
 * SWMM rejects a `[SUBCATCHMENTS]` row whose Raingage or Outlet column
 * points at nothing, so those deletions must be blocked, not allowed
 * outright.
 */
export function deleteElement(
  raw: ReadonlyMap<string, string[][]>,
  ref: ElementRef
): Map<string, string[][]> {
  const next = new Map(raw);
  for (const section of ownedSections(ref.kind, ref.category)) {
    const rows = next.get(section);
    if (rows) next.set(section, removeRowsByName(rows, ref.name));
  }
  const tagsType = TAGS_OBJECT_TYPE[ref.category];
  const tagsRows = tagsType ? next.get("TAGS") : undefined;
  if (tagsType && tagsRows) next.set("TAGS", removeTagsRow(tagsRows, tagsType, ref.name));
  return next;
}

/** Used to decide whether deleting a node is currently allowed at all (blocked while non-empty). */
export function linksAttachedToNode(project: SwmmProject, nodeName: string): SwmmLink[] {
  return project.links.filter((link) => link.fromNode === nodeName || link.toNode === nodeName);
}

/** Which of `link`'s own two end nodes would be left with ZERO other attached links if `link` itself were deleted. */
export function nodesOrphanedByDeletingLink(project: SwmmProject, link: SwmmLink): SwmmNode[] {
  const endpoints = new Set([link.fromNode, link.toNode]);
  return project.nodes.filter(
    (node) =>
      endpoints.has(node.name) &&
      linksAttachedToNode(project, node.name).every((l) => l.name === link.name)
  );
}

export function subcatchmentsUsingRaingage(
  project: SwmmProject,
  raingageName: string
): SwmmSubcatchment[] {
  return project.subcatchments.filter((s) => s.raingage === raingageName);
}

/** An Outlet can be EITHER a node or another subcatchment, so this checks both a to-be-deleted NODE and a to-be-deleted SUBCATCHMENT the same way. */
export function subcatchmentsUsingAsOutlet(project: SwmmProject, name: string): SwmmSubcatchment[] {
  return project.subcatchments.filter((s) => s.outlet === name);
}

/**
 * A node explicitly IN the selection is BLOCKED outright if it has any
 * attached link NOT also in the batch. A node NOT in the selection becomes
 * ORPHANED once every one of its attached links turns out to be in the
 * batch, even when no single one of those links would have orphaned it
 * alone. Never deletes anything itself.
 */
export function planGroupDelete(
  project: SwmmProject,
  selection: ElementRef[]
): { blockedNodes: SwmmNode[] } | { orphanedNodes: SwmmNode[] } {
  const selectedLinkNames = new Set(
    selection.filter((r) => r.category === "link").map((r) => r.name)
  );
  const selectedNodeNames = new Set(
    selection.filter((r) => r.category === "node").map((r) => r.name)
  );

  const blockedNodes = project.nodes.filter(
    (n) =>
      selectedNodeNames.has(n.name) &&
      linksAttachedToNode(project, n.name).some((l) => !selectedLinkNames.has(l.name))
  );
  if (blockedNodes.length > 0) return { blockedNodes };

  const candidateNodeNames = new Set<string>();
  for (const linkRef of selection.filter((r) => r.category === "link")) {
    const link = project.links.find((l) => l.name === linkRef.name);
    if (!link) continue;
    candidateNodeNames.add(link.fromNode);
    candidateNodeNames.add(link.toNode);
  }
  const orphanedNodes = project.nodes.filter((n) => {
    if (!candidateNodeNames.has(n.name) || selectedNodeNames.has(n.name)) return false;
    const attached = linksAttachedToNode(project, n.name);
    return attached.length > 0 && attached.every((l) => selectedLinkNames.has(l.name));
  });
  return { orphanedNodes };
}

function allElementNames(raw: ReadonlyMap<string, string[][]>): Set<string> {
  const names = new Set<string>();
  for (const rows of raw.values()) for (const row of rows) if (row[0]) names.add(row[0]);
  return names;
}

/** Shared by addJunction/addRaingage/addOutfall/addStorage/addDivider — each places one point-element row plus its own single point (COORDINATES for nodes, SYMBOLS for raingages), unlike addOpeningLink's link rows (two node references, no point of their own). */
function addPointElement(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  prefix: string,
  idIncrement: number,
  point: Point,
  rowFields: string[],
  pointSection: "COORDINATES" | "SYMBOLS" = "COORDINATES"
): { raw: Map<string, string[][]>; name: string } {
  const name = nextAvailableName(allElementNames(raw), prefix, idIncrement);
  const rows = cloneSection(raw, section);
  rows.push([name, ...rowFields]);
  let next = new Map(raw);
  next.set(section, rows);
  next = setSinglePoint(next, pointSection, name, point);
  return { raw: next, name };
}

/** Init Depth/Surcharge Depth stay 0 — the real GUI's own Project Defaults dialog never defaults those two either. */
export function addJunction(
  raw: ReadonlyMap<string, string[][]>,
  point: Point,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addPointElement(
    raw,
    "JUNCTIONS",
    defaults.idPrefixes.junction,
    defaults.idIncrement,
    point,
    [
      String(defaults.node.invert),
      String(defaults.node.maxDepth),
      "0",
      "0",
      String(defaults.node.pondedArea),
    ]
  );
}

export function addRaingage(
  raw: ReadonlyMap<string, string[][]>,
  point: Point,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addPointElement(
    raw,
    "RAINGAGES",
    defaults.idPrefixes.raingage,
    defaults.idIncrement,
    point,
    ["INTENSITY", "1:00", "1.0", "TIMESERIES", ""],
    "SYMBOLS"
  );
}

/** Does NOT validate that `fromNode`/`toNode` exist — the map only ever offers this action by clicking two real nodes. */
export function addConduit(
  raw: ReadonlyMap<string, string[][]>,
  fromNode: string,
  toNode: string,
  length?: number,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  const name = nextAvailableName(
    allElementNames(raw),
    defaults.idPrefixes.conduit,
    defaults.idIncrement
  );
  const conduits = cloneSection(raw, "CONDUITS");
  conduits.push([
    name,
    fromNode,
    toNode,
    length !== undefined ? String(length) : String(defaults.conduit.length),
    String(defaults.conduit.roughness),
    "0",
    "0",
    "0",
    "0",
  ]);
  const xsections = cloneSection(raw, "XSECTIONS");
  xsections.push([name, "CIRCULAR", "1", "0", "0", "0", "1"]);

  const next = new Map(raw);
  next.set("CONDUITS", conduits);
  next.set("XSECTIONS", xsections);
  return { raw: next, name };
}

/** FREE type — no stage-data column needed. */
export function addOutfall(
  raw: ReadonlyMap<string, string[][]>,
  point: Point,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addPointElement(
    raw,
    "OUTFALLS",
    defaults.idPrefixes.outfall,
    defaults.idIncrement,
    point,
    [String(defaults.node.invert), "FREE", "NO"]
  );
}

/** FUNCTIONAL shape with a non-zero surface-area coefficient (1000) — 0 would make the unit hold zero volume at any depth. */
export function addStorage(
  raw: ReadonlyMap<string, string[][]>,
  point: Point,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addPointElement(raw, "STORAGE", defaults.idPrefixes.storage, defaults.idIncrement, point, [
    String(defaults.node.invert),
    String(defaults.node.maxDepth),
    "0",
    "FUNCTIONAL",
    "1000",
    "0",
    "0",
    "0",
  ]);
}

/** Diverted Link left as `"*"` — SWMM's own placeholder token for "not yet specified", since a brand new divider has no outgoing conduit yet to name. */
export function addDivider(
  raw: ReadonlyMap<string, string[][]>,
  point: Point,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addPointElement(
    raw,
    "DIVIDERS",
    defaults.idPrefixes.divider,
    defaults.idIncrement,
    point,
    [String(defaults.node.invert), "*", "CUTOFF", "0", "0", "0", "0", "0"]
  );
}

/** No [XSECTIONS] entry, unlike every other link kind this file adds — a pump's flow is governed entirely by its curve, not an opening shape. Pump Curve left as `"*"` (not yet specified). */
export function addPump(
  raw: ReadonlyMap<string, string[][]>,
  fromNode: string,
  toNode: string,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  const name = nextAvailableName(
    allElementNames(raw),
    defaults.idPrefixes.pump,
    defaults.idIncrement
  );
  const rows = cloneSection(raw, "PUMPS");
  rows.push([name, fromNode, toNode, "*", "ON", "0", "0"]);
  const next = new Map(raw);
  next.set("PUMPS", rows);
  return { raw: next, name };
}

/** Shared by addOrifice/addWeir/addOutlet — each pushes its own section's row plus a companion [XSECTIONS] row, CIRCULAR/1, unlike addPump (a pump has no opening to shape at all). */
function addOpeningLink(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  prefix: string,
  idIncrement: number,
  fromNode: string,
  toNode: string,
  typeAndParams: string[]
): { raw: Map<string, string[][]>; name: string } {
  const name = nextAvailableName(allElementNames(raw), prefix, idIncrement);
  const rows = cloneSection(raw, section);
  rows.push([name, fromNode, toNode, ...typeAndParams]);
  const xsections = cloneSection(raw, "XSECTIONS");
  xsections.push([name, "CIRCULAR", "1", "0", "0", "0", "1"]);
  const next = new Map(raw);
  next.set(section, rows);
  next.set("XSECTIONS", xsections);
  return { raw: next, name };
}

/** SIDE configuration, 0.65 discharge coefficient — 0 would pass zero flow. */
export function addOrifice(
  raw: ReadonlyMap<string, string[][]>,
  fromNode: string,
  toNode: string,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addOpeningLink(
    raw,
    "ORIFICES",
    defaults.idPrefixes.orifice,
    defaults.idIncrement,
    fromNode,
    toNode,
    ["SIDE", "0", "0.65", "NO", "0"]
  );
}

/** TRANSVERSE type, 3.3 discharge coefficient — 0 would pass zero flow. */
export function addWeir(
  raw: ReadonlyMap<string, string[][]>,
  fromNode: string,
  toNode: string,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addOpeningLink(
    raw,
    "WEIRS",
    defaults.idPrefixes.weir,
    defaults.idIncrement,
    fromNode,
    toNode,
    ["TRANSVERSE", "0", "3.3", "NO", "0", "0"]
  );
}

/** FUNCTIONAL/DEPTH rating with a non-zero flow coefficient/exponent — 0 would pass zero flow. */
export function addOutlet(
  raw: ReadonlyMap<string, string[][]>,
  fromNode: string,
  toNode: string,
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  return addOpeningLink(
    raw,
    "OUTLETS",
    defaults.idPrefixes.outlet,
    defaults.idIncrement,
    fromNode,
    toNode,
    ["0", "FUNCTIONAL/DEPTH", "1", "1", "NO"]
  );
}

/** Empty string if the project has no nodes yet. */
function nearestNodeName(raw: ReadonlyMap<string, string[][]>, point: Point): string {
  let best: string | null = null;
  let bestDistance = Infinity;
  for (const row of raw.get("COORDINATES") ?? []) {
    const x = Number(row[1]);
    const y = Number(row[2]);
    // Same finiteness guard as parseInp.ts's own COORDINATES reading — a
    // malformed row must be skipped explicitly rather than relying on NaN
    // comparisons always being false, which would silently exclude it here
    // without ever spelling out why.
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const dx = x - point.x;
    const dy = y - point.y;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = row[0];
    }
  }
  return best ?? "";
}

/** Raingage/Outlet are the exception to the usual placeholder-0 convention, since "" would leave an obviously-broken subcatchment behind: Raingage defaults to the project's first raingage if one exists, Outlet to whichever node is geometrically closest to the polygon's own centroid. */
export function addSubcatchment(
  raw: ReadonlyMap<string, string[][]>,
  polygon: Point[],
  defaults: ElementDefaults = DEFAULT_ELEMENT_DEFAULTS
): { raw: Map<string, string[][]>; name: string } {
  const name = nextAvailableName(
    allElementNames(raw),
    defaults.idPrefixes.subcatchment,
    defaults.idIncrement
  );
  const raingage = (raw.get("RAINGAGES") ?? [])[0]?.[0] ?? "";
  const outlet = nearestNodeName(raw, polygonCentroid(polygon));

  const { subcatchment: sc } = defaults;
  const subcatchments = cloneSection(raw, "SUBCATCHMENTS");
  subcatchments.push([
    name,
    raingage,
    outlet,
    String(sc.area),
    String(sc.imperviousPercent),
    String(sc.width),
    String(sc.slopePercent),
    "0",
  ]);
  const subareas = cloneSection(raw, "SUBAREAS");
  subareas.push([
    name,
    String(sc.nImperv),
    String(sc.nPerv),
    String(sc.dStoreImperv),
    String(sc.dStorePerv),
    String(sc.pctZeroImperv),
    "OUTLET",
    "0",
  ]);
  const infiltration = cloneSection(raw, "INFILTRATION");
  infiltration.push([name, "0", "0", "0", "0", "0"]);
  const polygons = cloneSection(raw, "POLYGONS");
  for (const point of polygon) polygons.push([name, String(point.x), String(point.y)]);

  const next = new Map(raw);
  next.set("SUBCATCHMENTS", subcatchments);
  next.set("SUBAREAS", subareas);
  next.set("INFILTRATION", infiltration);
  next.set("POLYGONS", polygons);
  return { raw: next, name };
}

/** A link's [VERTICES] rows or a subcatchment's [POLYGONS] rows — both share the same `Name X Y` row shape, so one set of vertex mutations covers both. */
export interface VertexTarget {
  section: "VERTICES" | "POLYGONS";
  name: string;
}

/** The seam between "index within this element's own point list" (what the UI thinks in) and "index within the whole section's rows" (what actually needs splicing). */
function rowIndicesForName(rows: string[][], name: string): number[] {
  const indices: number[] = [];
  rows.forEach((row, i) => {
    if (row[0] === name) indices.push(i);
  });
  return indices;
}

export function moveVertex(
  raw: ReadonlyMap<string, string[][]>,
  target: VertexTarget,
  index: number,
  point: Point
): ReadonlyMap<string, string[][]> {
  const rows = cloneSection(raw, target.section);
  const rowIndex = rowIndicesForName(rows, target.name)[index];
  if (rowIndex === undefined) return raw;
  rows[rowIndex] = [target.name, String(point.x), String(point.y)];
  const next = new Map(raw);
  next.set(target.section, rows);
  return next;
}

/** Shifts whatever was already at `index` (and everything after it) up by one — `index` equal to the current point count appends at the end. */
export function insertVertexAt(
  raw: ReadonlyMap<string, string[][]>,
  target: VertexTarget,
  index: number,
  point: Point
): ReadonlyMap<string, string[][]> {
  const rows = cloneSection(raw, target.section);
  const indices = rowIndicesForName(rows, target.name);
  const insertAt = index < indices.length ? indices[index] : (indices.at(-1) ?? -1) + 1;
  rows.splice(insertAt, 0, [target.name, String(point.x), String(point.y)]);
  const next = new Map(raw);
  next.set(target.section, rows);
  return next;
}

/** The map only offers this once at least 4 points remain for a polygon, so a delete never collapses a shape below a valid triangle — nothing here re-checks that. */
export function removeVertexAt(
  raw: ReadonlyMap<string, string[][]>,
  target: VertexTarget,
  index: number
): ReadonlyMap<string, string[][]> {
  const rows = cloneSection(raw, target.section);
  const rowIndex = rowIndicesForName(rows, target.name)[index];
  if (rowIndex === undefined) return raw;
  rows.splice(rowIndex, 1);
  const next = new Map(raw);
  next.set(target.section, rows);
  return next;
}
